import { getDictionary } from "@/src/dictionary";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from '@contentful/rich-text-types';
import Image from 'next/image';
import Link from "next/link";
import { findTalentBySlug } from "../utils/search-controls";

const InstagramSVG = () => {
    return (
        <svg width="22" height="24" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_795_880)">
                <path d="M10.7001 6.60977C7.6633 6.60977 5.21381 9.01445 5.21381 11.9957C5.21381 14.977 7.6633 17.3816 10.7001 17.3816C13.7369 17.3816 16.1864 14.977 16.1864 11.9957C16.1864 9.01445 13.7369 6.60977 10.7001 6.60977ZM10.7001 15.4973C8.73764 15.4973 7.13329 13.927 7.13329 11.9957C7.13329 10.0645 8.73287 8.49414 10.7001 8.49414C12.6673 8.49414 14.2669 10.0645 14.2669 11.9957C14.2669 13.927 12.6626 15.4973 10.7001 15.4973ZM17.6905 6.38945C17.6905 7.08789 17.1175 7.6457 16.4108 7.6457C15.6994 7.6457 15.1312 7.0832 15.1312 6.38945C15.1312 5.6957 15.7041 5.1332 16.4108 5.1332C17.1175 5.1332 17.6905 5.6957 17.6905 6.38945ZM21.3241 7.66445C21.243 5.98164 20.8514 4.49102 19.5956 3.26289C18.3446 2.03477 16.8262 1.65039 15.1121 1.56602C13.3454 1.46758 8.05006 1.46758 6.28337 1.56602C4.57398 1.6457 3.05558 2.03008 1.79979 3.2582C0.544007 4.48633 0.157245 5.97695 0.0712976 7.65977C-0.0289742 9.39414 -0.0289742 14.5926 0.0712976 16.327C0.15247 18.0098 0.544007 19.5004 1.79979 20.7285C3.05558 21.9566 4.5692 22.341 6.28337 22.4254C8.05006 22.5238 13.3454 22.5238 15.1121 22.4254C16.8262 22.3457 18.3446 21.9613 19.5956 20.7285C20.8466 19.5004 21.2382 18.0098 21.3241 16.327C21.4244 14.5926 21.4244 9.39883 21.3241 7.66445ZM19.0418 18.1879C18.6693 19.1066 17.9483 19.8145 17.0077 20.1848C15.5991 20.7332 12.2567 20.6066 10.7001 20.6066C9.1435 20.6066 5.79634 20.7285 4.39253 20.1848C3.45666 19.8191 2.73566 19.1113 2.35845 18.1879C1.79979 16.8051 1.92871 13.5238 1.92871 11.9957C1.92871 10.4676 1.80457 7.18164 2.35845 5.80352C2.73089 4.88477 3.45189 4.17695 4.39253 3.80664C5.80111 3.2582 9.1435 3.38477 10.7001 3.38477C12.2567 3.38477 15.6039 3.26289 17.0077 3.80664C17.9435 4.17227 18.6645 4.88008 19.0418 5.80352C19.6004 7.18633 19.4715 10.4676 19.4715 11.9957C19.4715 13.5238 19.6004 16.8098 19.0418 18.1879Z" fill="white" />
            </g>
            <defs>
                <clipPath id="clip0_795_880">
                    <rect width="21.3913" height="24" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
}

const XSVG = () => {
    return (
        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.64 2.25H22.0213L14.6361 10.5094L23.324 21.75H16.5231L11.1925 14.9344L5.10036 21.75H1.71425L9.61197 12.9141L1.2832 2.25H8.25657L13.0699 8.47969L18.64 2.25ZM17.4522 19.7719H19.3249L7.23643 4.125H5.22488L17.4522 19.7719Z" fill="white" />
        </svg>
    );
}

// renders H2 elements to actual H2 tags (instead of P) from Rich Text Contentful fields
const renderOptions = {
    renderNode: {
        [BLOCKS.HEADING_2]: (node: any, children: any) => {
            return <h2 className='font-medium text-uppercase mb-5 text-hot-yellow font-[Judge] pt-8 text-6xl'>{children}</h2>
        },
    },
}

function getSocialHandle(url: string) {
    return url.substring(url.lastIndexOf('/') + 1)
}

interface TalentProps {
    talent?: any,
    talents: any[]
}

const Talent = ({ talent, talents }: TalentProps) => {

    return (
        talent ? (
            <main className="min-h-[100vh]">
                <div className='flex flex-col lg:flex-row-reverse lg:justify-between'>
                    <div
                        className='bg-no-repeat bg-cover bg-top min-h-[600px] w-full'
                        style={{
                            backgroundImage: `url(${'https:' + talent.fields.profilePic.fields.file.url
                            })`,
                        }}
                     data-ll-status='loaded'
                     />
                    <div className='p-4 lg:p-24 lg:b-black lg:w-2/3'>
                        <h1 className={` text-content flex-1 items-center pb-4 hidden lg:flex`}>
                            <span className='pr-1 font-[Judge]'>Talent</span>
                            <div className={`border-b-2 flex-1 border-content`}></div>
                        </h1>
                        <h1 className={`text-content font-[Judge] lg:text-hot-yellow text-4xl`}>{talent.fields.name}</h1>
                        {talent.fields.hasOwnProperty('shows')
                            ? talent.fields.shows.map((show: any) => {
                                return (
                                    <div key={show.sys.id} className='pt-6'>
                                        <p className='text-hot-yellow font-[Judge] leading-none my-1'>{show.fields.name}</p>
                                        <p className={`font-[Judge] leading-none text-content my-1`}>
                                            {show.fields.schedule}
                                        </p>
                                    </div>
                                )
                            })
                            : null}
                        <div className='flex flex-col mt-8 justify-start gap-2 sm:flex-row'>
                            {
                                talent.fields.hasOwnProperty('socials') ?
                                    talent.fields.socials.map((social: any) => {
                                        if (social.includes('instagram')) {
                                            return (
                                                <Link prefetch={false}
                                                    key={social}
                                                    href={social}
                                                    target='_blank'
                                                    aria-label={'Instagram link for ' + talent.name}
                                                    className='text-content'>
                                                    <div className='flex flex-row justify-start mr-6'>
                                                        <InstagramSVG />
                                                        <span className="text-sm text-content p-1">{getSocialHandle(social)}</span>
                                                    </div>
                                                </Link>
                                            )
                                        } else if (social.includes('twitter')) {
                                            return (
                                                <Link prefetch={false}
                                                    key={social}
                                                    href={social}
                                                    target='_blank'
                                                    aria-label={'Twitter link for ' + talent.name}>
                                                    <div className='flex flex-row justify-start mr-6'>
                                                        <XSVG />
                                                        <span className="text-sm text-content p-1">{getSocialHandle(social)}</span>
                                                    </div>
                                                </Link>
                                            )
                                        }
                                    }) : null
                            }
                        </div>
                        <div className='w-full lg:pr-12 pt-4'>
                            <div className={`text-content font-[Oskari]`}>
                                {documentToReactComponents(talent.fields.bio, renderOptions)}
                            </div>
                    </div>
                    </div>
                    
                </div>
                <main id='main' className='max-w-1440 md:mx-auto md:flex md:items-start flex-col lg:flex-row lg:pl-24 lg:pr-0'>
                    <aside className='bg-body w-full flex-2'>
                        {
                            talent.fields.hasOwnProperty('cohosts')
                                ?
                                <div className="flex flex-col bg-body py-4 lg:px-0 px-4 lg:justify-start lg:items-start">
                                    <h1 className={`text-content flex flex-1 items-center pb-4 lg:bg-hot-yellow lg:p-4 w-full font-[Judge]`}>
                                        <span className='pr-1 lg:text-black sm:text-hot-yellow'>{talent.fields.name + '\'s co-hosts'}</span>
                                        <div className="border-b-2 flex-1 lg:hidden"></div>
                                    </h1>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:flex lg:flex-row lg:justify-center lg:items-stretch lg:mt-4">
                                        {
                                            talent.fields.cohosts.map((cohostSlug: string) => {
                                                const cohost = findTalentBySlug(talents, cohostSlug) as any
                                                debugger
                                                return (
                                                    cohost && cohost.fields ?
                                                        (<div key={cohost.fields.slug} className='flex flex-col items-stretch min-w-[200px] max-w-[400px] min-h-[200px]'>
                                                            <Image
                                                                src={`https:${cohost.fields.profilePic.fields.file.url}?fit=scale&q=100`}
                                                                className='w-full'
                                                                alt={cohost.fields.name}
                                                                data-ll-status='loaded'
                                                                width='600'
                                                                height='300'
                                                            />
                                                            <div className='flex-1 bg-black p-8'>
                                                                <Link href={getDictionary('talentPath') + cohost.fields.slug}>
                                                                    <h1 className="font-[Judge] text-hot-yellow text-3xl">{cohost.fields.name}</h1>
                                                                </Link>
                                                            </div>
                                                        </div>
                                                        ) : null
                                                )
                                            })
                                        }
                                    </div>

                                </div>
                                : null
                        }
                    </aside>
                </main>

            </main>) : null
    )
}

export default Talent
